import type { MemberSearchSession } from '@affine/core/modules/permissions';
import { UserListServiceExtension } from '@blocksuite/affine/shared/services';
import { LifeCycleWatcher } from '@blocksuite/std';
import type { ExtensionType } from '@blocksuite/store';

/**
 * sl-p64h.4b: Returns both the UserList service extension and a
 * LifeCycleWatcher that disposes the per-editor MemberSearchSession on
 * `unmounted()`. ViewExtensionProvider.setup() runs once per editor
 * instance and never gets a teardown hook — without this watcher every
 * editor mount would leak a Session (rxjs effect subscription).
 */
export function patchUserListExtensions(
  session: MemberSearchSession
): ExtensionType[] {
  class MemberSearchSessionLifecycle extends LifeCycleWatcher {
    static override readonly key = 'member-search-session-lifecycle';
    override unmounted() {
      session.dispose();
    }
  }

  return [
    UserListServiceExtension({
      // eslint-disable-next-line rxjs/finnish
      hasMore$: session.hasMore$.signal,
      loadMore() {
        session.loadMore();
      },
      // eslint-disable-next-line rxjs/finnish
      isLoading$: session.isLoading$.signal,
      // eslint-disable-next-line rxjs/finnish
      searchText$: session.searchText$.signal,
      search(keyword) {
        session.search(keyword);
      },
      // eslint-disable-next-line rxjs/finnish
      users$: session.result$.map(users =>
        users.map(u => ({
          id: u.id,
          name: u.name,
          avatar: u.avatarUrl,
        }))
      ).signal,
    }),
    MemberSearchSessionLifecycle,
  ];
}
